import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaChevronLeft, FaChevronRight, FaSyncAlt } from "react-icons/fa";
import { downloadCSV, downloadPDF, printThermal } from "../utils/exportHelpers";

const COLOR = {
  primary: "#015b9a",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
};

const defaultData = (date) => ({
  date,
  restaurant: "",
  total_items: 0,
  total_quantity: 0,
  total_amount: 0,
  total_orders: 0,
  items: [],
});

const DayWiseItemReportPage = () => {
  const [userId, setUserId] = useState(null);
  const [date, setDate] = useState(today());
  const [data, setData] = useState(defaultData(today()));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const reportRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}").data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
  }, []);

  useEffect(() => {
    if (userId) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, date]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    try {
      const res = await fetch(
        "https://qr.billsarthi.in/public/api/report-v2/day-wise-items",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: userId, date }),
        }
      );
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.message || "Failed to load report");
      setData({
        ...defaultData(date),
        ...json,
        items: Array.isArray(json?.items) ? json.items : [],
      });
      setPage(0);
    } catch (e) {
      toast.error(e.message || "Unable to load report");
      setData(defaultData(date));
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const changeDate = (delta) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    setDate(toISO(d));
  };

  const fmtINR = (n) =>
    `₹${Number(n || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return { start, end, rows: data.items.slice(start, end) };
  }, [data.items, page]);

  const pageCount = Math.ceil((data.items.length || 0) / rowsPerPage);

  const exportCsv = () => {
    const rows = data.items.map((it, idx) => [
      idx + 1,
      it.item_name,
      it.category_name || "Uncategorized",
      it.total_quantity,
      it.total_orders,
      it.total_amount,
    ]);
    downloadCSV(
      `day_wise_items_${date}.csv`,
      ["#", "Item", "Category", "Qty", "Orders", "Amount"],
      rows
    );
  };

  const exportPdf = () => downloadPDF(reportRef.current, `day_wise_items_${date}.pdf`);

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <Top>
        <div>
          <Title>Day-wise Item Report</Title>
          <Sub>Sales by item for the selected date.</Sub>
          <PillRow>
            <Pill>Total Items: {data.total_items || 0}</Pill>
            <Pill>Total Qty: {data.total_quantity || 0}</Pill>
            <Pill>Total Orders: {data.total_orders || 0}</Pill>
            <Pill>Total Amount: {fmtINR(data.total_amount)}</Pill>
          </PillRow>
        </div>
        <Actions>
          <NavBtn onClick={() => changeDate(-1)}>
            <FaChevronLeft />
          </NavBtn>
          <DateInput>
            Date:
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </DateInput>
          <NavBtn onClick={() => changeDate(1)}>
            <FaChevronRight />
          </NavBtn>
          <PrimaryBtn onClick={() => fetchReport({ refresh: true })}>
            <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
          </PrimaryBtn>
        </Actions>
      </Top>

      <Card ref={reportRef}>
        {loading ? (
          <Center>Loading…</Center>
        ) : data.items.length === 0 ? (
          <Center>No data for this date.</Center>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item</th>
                  <th>Category</th>
                  <th>Qty</th>
                  <th>Orders</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {paginated.rows.map((it, idx) => (
                  <tr key={it.product_id || idx}>
                    <td>{paginated.start + idx + 1}</td>
                    <td>{it.item_name}</td>
                    <td>{it.category_name || "Uncategorized"}</td>
                    <td>{it.total_quantity}</td>
                    <td>{it.total_orders}</td>
                    <td>{fmtINR(it.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}

        <Pager>
          <PageBtn disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            ‹ Prev
          </PageBtn>
          <PageInfo>
            Page <strong>{pageCount ? page + 1 : 0}</strong> of <strong>{pageCount}</strong>
          </PageInfo>
          <PageBtn disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)}>
            Next ›
          </PageBtn>
        </Pager>

        <Actions style={{ marginTop: 10 }}>
          <PrimaryBtn onClick={exportPdf}>PDF</PrimaryBtn>
          <PrimaryBtn onClick={() => printThermal(reportRef.current, `day_wise_items_${date}`)}>
            Thermal Print
          </PrimaryBtn>
          <PrimaryBtn onClick={exportCsv}>CSV</PrimaryBtn>
        </Actions>
      </Card>
    </Page>
  );
};

export default DayWiseItemReportPage;

const today = () => toISO(new Date());
const toISO = (d) => d.toISOString().slice(0, 10);

const Page = styled.div`
  background: ${COLOR.bg};
  min-height: 100vh;
  padding: 16px;
`;
const Top = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;
const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color: ${COLOR.text};
`;
const Sub = styled.div`
  color: ${COLOR.muted};
`;
const PillRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`;
const Pill = styled.span`
  background: #eef2ff;
  color: ${COLOR.text};
  padding: 8px 10px;
  border-radius: 12px;
  font-weight: 800;
  border: 1px solid ${COLOR.line};
`;
const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;
const DateInput = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f9f9f9;
  color: ${COLOR.text};
  input {
    border: 0;
    background: transparent;
    outline: none;
  }
`;
const NavBtn = styled.button`
  border: 1px solid ${COLOR.line};
  background: #fff;
  color: ${COLOR.primary};
  border-radius: 10px;
  padding: 8px 10px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`;
const PrimaryBtn = styled.button`
  background: ${COLOR.primary};
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;
const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 16px;
`;
const Center = styled.div`
  text-align: center;
  color: ${COLOR.muted};
  padding: 20px 0;
`;
const TableWrap = styled.div`
  overflow: auto;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 10px;
    border-bottom: 1px solid ${COLOR.line};
    text-align: left;
    font-size: 14px;
  }
  th {
    background: #f1f5f9;
    font-weight: 800;
  }
`;
const Pager = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  flex-wrap: wrap;
`;
const PageBtn = styled.button`
  background: #fff;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const PageInfo = styled.div`
  color: ${COLOR.muted};
`;
